import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { MapPin, Briefcase, DollarSign, Sparkles, GraduationCap, Building2, ExternalLink, Bookmark, Flag } from "lucide-react";
import { toast, Toaster } from "sonner";

export default function JobDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [job, setJob] = useState(null);
  const [applying, setApplying] = useState(false);
  const [coverNote, setCoverNote] = useState("");
  const [saved, setSaved] = useState(false);
  const [applied, setApplied] = useState(false);

  useEffect(()=>{
    api.get(`/jobs/${id}`).then((r)=>setJob(r.data)).catch(()=>setJob(false));
    if (user?.role === "youth") {
      api.get("/me/saved-jobs").then((r)=>{
        setSaved(!!(r.data.jobs || []).find(j=>j.id===id));
      }).catch(()=>{});
      api.get("/me/applications").then((r)=>{
        setApplied(!!(r.data.applications || []).find(a=>a.job_id===id));
      }).catch(()=>{});
    }
  }, [id, user]);

  if (job === null) return <div className="nh-container py-24 text-center">Loading…</div>;
  if (job === false) return <div className="nh-container py-24 text-center">Job not found.</div>;

  const ageOK = !user?.age || (user.age >= job.min_age && user.age <= job.max_age);
  const pay = job.pay_min || job.pay_max
    ? `$${job.pay_min || 0}${job.pay_max ? `–$${job.pay_max}` : ""}/${(job.pay_period||"hr").slice(0,3)}`
    : (job.job_type === "volunteer" ? "Volunteer role" : "Pay TBD");

  const toggleSave = async () => {
    if (!user) return nav("/login");
    try {
      if (saved) { await api.delete(`/jobs/${id}/save`); setSaved(false); toast("Removed from saved"); }
      else { await api.post(`/jobs/${id}/save`); setSaved(true); toast.success("Saved!"); }
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const apply = async () => {
    if (!user) return nav("/login");
    if (user.role !== "youth") return toast.error("Only youth accounts can apply.");
    setApplying(true);
    try {
      await api.post("/applications", { job_id: id, cover_note: coverNote });
      setApplied(true);
      toast.success("Application sent!");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setApplying(false); }
  };

  const report = async () => {
    if (!user) return nav("/login");
    const reason = prompt("Why are you reporting this job?");
    if (!reason) return;
    try { await api.post("/reports", { target_type: "job", target_id: id, reason }); toast.success("Reported. Thanks."); }
    catch(e){ toast.error(formatApiError(e)); }
  };

  return (
    <div className="nh-container py-10 grid md:grid-cols-12 gap-8">
      <Toaster position="top-center"/>
      <div className="md:col-span-8">
        <Link to="/jobs" className="text-sm underline">← Back to jobs</Link>
        <div className="nh-card p-8 mt-3">
          <div className="flex items-start gap-4">
            <div className="h-14 w-14 rounded-lg border-2 border-black bg-[var(--nh-accent)] flex items-center justify-center font-black text-2xl">
              {(job.company_name||"N")[0]}
            </div>
            <div>
              <h1 className="font-display font-black text-3xl leading-tight">{job.title}</h1>
              <p className="text-[var(--nh-muted)] mt-1"><Building2 size={14} className="inline mr-1"/>{job.company_name}</p>
            </div>
          </div>

          <div className="mt-5 flex flex-wrap gap-2 text-xs">
            <span className="nh-tag bg-white"><Briefcase size={12} className="mr-1"/>{job.job_type}</span>
            <span className="nh-tag bg-white"><MapPin size={12} className="mr-1"/>{job.remote ? "Remote" : job.location}</span>
            <span className="nh-tag bg-white"><DollarSign size={12} className="mr-1"/>{pay}</span>
            <span className="nh-tag bg-[var(--nh-mint)]"><GraduationCap size={12} className="mr-1"/>Ages {job.min_age}–{job.max_age}</span>
            {!job.experience_required && <span className="nh-tag bg-[var(--nh-pink)]"><Sparkles size={12} className="mr-1"/>No experience</span>}
            {job.work_permit_required && <span className="nh-tag bg-[var(--nh-sky)]">Work permit required</span>}
            {job.school_friendly && <span className="nh-tag bg-[var(--nh-mint)]">School-friendly</span>}
            {job.source && <span className="nh-tag bg-white">via {job.source}</span>}
          </div>

          <div className="prose-nh mt-6 text-[var(--nh-muted)] whitespace-pre-line" dangerouslySetInnerHTML={{ __html: job.description || "" }}/>

          {(job.tags||[]).length > 0 && (
            <div className="mt-6 flex flex-wrap gap-2">
              {job.tags.map((t)=>(<span key={t} className="nh-tag bg-white">#{t}</span>))}
            </div>
          )}
        </div>
      </div>

      {/* Sticky apply card */}
      <aside className="md:col-span-4">
        <div className="nh-card p-6 sticky top-24">
          {!ageOK && (
            <div className="nh-card bg-[var(--nh-primary)] text-white p-3 mb-4 shadow-none">
              <p className="text-sm font-bold">You're not age-eligible for this role (needs {job.min_age}–{job.max_age}).</p>
            </div>
          )}
          {applied ? (
            <div className="nh-card bg-[var(--nh-mint)] p-4 shadow-none">
              <p className="font-display font-black">Application sent!</p>
              <p className="text-sm mt-1">Check status in your <Link className="underline" to="/dashboard">dashboard</Link>.</p>
            </div>
          ) : (
            <>
              <label className="text-xs uppercase font-bold tracking-wide">Cover note (optional)</label>
              <textarea rows={4} className="nh-input mt-1" placeholder="Why are you excited about this role?"
                value={coverNote} onChange={(e)=>setCoverNote(e.target.value)} data-testid="apply-cover-note"/>
              <button onClick={apply} disabled={!ageOK || applying} className="nh-btn nh-btn-primary w-full mt-3" data-testid="apply-btn">
                {applying ? "Sending…" : "Apply Now"}
              </button>
              {job.apply_url && (
                <a href={job.apply_url} target="_blank" rel="noreferrer" className="nh-btn nh-btn-ghost w-full mt-2 text-xs" data-testid="apply-external">
                  <ExternalLink size={14}/> Apply on external site
                </a>
              )}
            </>
          )}
          <div className="flex gap-2 mt-3">
            <button onClick={toggleSave} className={`nh-btn ${saved ? "nh-btn-secondary" : "nh-btn-ghost"} flex-1 text-xs`} data-testid="save-btn">
              <Bookmark size={14}/> {saved ? "Saved" : "Save"}
            </button>
            <button onClick={report} className="nh-btn nh-btn-ghost text-xs" data-testid="report-btn">
              <Flag size={14}/> Report
            </button>
          </div>
          <p className="text-[10px] text-[var(--nh-muted)] mt-4 leading-relaxed">
            Safety tip: NextHire never asks you to pay for a job. Never share bank info or SSN before signing a contract.
          </p>
        </div>
      </aside>
    </div>
  );
}
